import type { Client, Room } from '@colyseus/sdk';
import type { Action, PlayerView, Role } from '@il-colpo/engine';
import { PROTOCOL_VERSION } from '@il-colpo/shared';
import {
  ClientMessages,
  ErrorCodeSchema,
  ROOM_NAME,
  ServerMessages,
  type ClientMessageOf,
  type ClientMessageType,
  type ErrorCode,
  type JoinOptions,
  type LobbyConfig,
  type ServerMessageOf,
  type ServerMessageType,
  type SettlementSummary,
} from './messages.ts';

/**
 * The client's end of the conversation with the game server, with no screen in it: it joins a room,
 * keeps what the server has said (the lobby, the latest view, the chat), sends what the player does,
 * and mends the connection when it drops. The app wraps it in stores and screens; the server's tests
 * use it as the real thing.
 *
 * Reconnection has two layers. The library reconnects by itself with a token (the server holds the
 * seat for the grace). If that gives up, this joins the room again by its code: the seat is still the
 * player's until the game is over, even after a bot has been playing it.
 */

export type ConnectionStatus =
  'idle' | 'connecting' | 'lobby' | 'playing' | 'over' | 'reconnecting' | 'closed';

/** What went wrong: one of the server's codes, or one of the client's own. */
export type Issue = ErrorCode | 'connectionLost' | 'unreachable';

export interface ChatEntry {
  from: string;
  name: string;
  text: string;
  at: number;
}

export interface EmoteEntry {
  from: string;
  name: string;
  id: ServerMessageOf<'emote'>['id'];
  at: number;
}

export interface ConnectionState {
  status: ConnectionStatus;
  /** What the status was before a drop, to go back to when the connection comes back. */
  code: string | null;
  mode: 'quick' | 'private' | 'ranked' | 'daily' | null;
  /** The player's seat. */
  you: string | null;
  lobby: ServerMessageOf<'lobby'> | null;
  /** When the lobby above arrived (milliseconds since the epoch): a countdown in it is counted from then. */
  lobbyAt: number;
  /** The latest view of the game (the player's own). */
  view: PlayerView | null;
  seq: number;
  chat: ChatEntry[];
  /** The latest emotes of the table, oldest first (the screens show those that are recent). */
  emotes: EmoteEntry[];
  /** The last thing that went wrong, with a time so that the same one twice is still news. */
  issue: { issue: Issue; detail?: string; at: number } | null;
  kicked: boolean;
  /** What the heist did for the player (XP, level, rating, trophies…), once the server has settled it. */
  settlement: SettlementSummary | null;
}

const INITIAL: ConnectionState = {
  status: 'idle',
  code: null,
  mode: null,
  you: null,
  lobby: null,
  lobbyAt: 0,
  view: null,
  seq: 0,
  chat: [],
  emotes: [],
  issue: null,
  kicked: false,
  settlement: null,
};

export interface ConnectionOptions {
  /** `ws://host:port` of the game server. */
  endpoint: string;
  /** The access token of the player's session (a fresh one each time it is asked). */
  token: () => Promise<string | null>;
  language: () => 'it' | 'en';
  /** The version of the app, told to the server at the door (it may refuse one that is too old). */
  appVersion?: () => string;
  /** Pauses between attempts to join again after the library gave up (tests make them short). */
  rejoinDelaysMs?: number[];
}

/** A join that did not happen, and why. */
export class JoinError extends Error {
  constructor(
    readonly issue: Issue,
    /** More about the refusal: for `alreadyInRoom`, the room the player is in. */
    readonly detail?: string,
  ) {
    super(issue);
    this.name = 'JoinError';
  }
}

/** A room id as Colyseus makes them, or a code of six characters: what is safe to join by. */
const ROOM_ID = /^[A-Za-z0-9_-]{1,32}$/;

const MAX_CHAT = 200;
const MAX_EMOTES = 20;
const CONSENTED = 4000;

export class GameConnection {
  private state: ConnectionState = INITIAL;
  private room: Room | null = null;
  private client: Client | null = null;
  private readonly listeners = new Set<() => void>();
  private ver = 0;
  private leaving = false;
  private recovering = false;

  constructor(private readonly options: ConnectionOptions) {}

  // ---- What is known ------------------------------------------------------------------

  getState(): ConnectionState {
    return this.state;
  }

  /** Bumped on every change, for `useSyncExternalStore`. */
  version(): number {
    return this.ver;
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private patch(change: Partial<ConnectionState>): void {
    this.state = { ...this.state, ...change };
    this.ver += 1;
    for (const l of [...this.listeners]) l();
  }

  private flag(issue: Issue, detail?: string): void {
    this.patch({ issue: { issue, ...(detail ? { detail } : {}), at: Date.now() } });
  }

  // ---- Getting in ---------------------------------------------------------------------

  /** A quick match: any open table of the player's language. */
  quickMatch(): Promise<void> {
    return this.open((client, base) =>
      client.joinOrCreate(ROOM_NAME, { ...base, mode: 'quick' } satisfies JoinOptions),
    );
  }

  /** Ranked play: any open ranked table of the player's language (four or five players, no bots). */
  rankedMatch(): Promise<void> {
    return this.open((client, base) =>
      client.joinOrCreate(ROOM_NAME, { ...base, mode: 'ranked' } satisfies JoinOptions),
    );
  }

  /** The daily heist: a table of your own, with the day's map and role and three bots. */
  dailyRun(): Promise<void> {
    return this.open((client, base) =>
      client.create(ROOM_NAME, { ...base, mode: 'daily' } satisfies JoinOptions),
    );
  }

  /** A private room, which the player hosts. */
  createRoom(config: Partial<LobbyConfig> = {}): Promise<void> {
    return this.open((client, base) =>
      client.create(ROOM_NAME, { ...base, mode: 'private', config } satisfies JoinOptions),
    );
  }

  /** A private room, by its code. */
  joinRoom(code: string): Promise<void> {
    return this.open((client, base) =>
      client.joinById(code, { ...base, mode: 'private' } satisfies JoinOptions),
    );
  }

  private async open(
    join: (client: Client, base: Omit<JoinOptions, 'mode'>) => Promise<Room>,
  ): Promise<void> {
    await this.close();
    this.patch({ ...INITIAL, status: 'connecting' });
    try {
      const room = await this.join(join);
      this.attach(room);
    } catch (error) {
      const issue = error instanceof JoinError ? error.issue : 'unreachable';
      this.patch({ status: 'closed' });
      this.flag(issue, error instanceof JoinError ? error.detail : undefined);
      throw error instanceof JoinError ? error : new JoinError(issue);
    }
  }

  private async join(
    join: (client: Client, base: Omit<JoinOptions, 'mode'>) => Promise<Room>,
  ): Promise<Room> {
    const token = await this.options.token();
    if (!token) throw new JoinError('unauthorized');
    try {
      const { Client } = await import('@colyseus/sdk');
      this.client ??= new Client(this.options.endpoint);
      return await join(this.client, {
        protocolVersion: PROTOCOL_VERSION,
        token,
        language: this.options.language(),
        ...(this.options.appVersion ? { clientVersion: this.options.appVersion() } : {}),
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : '';
      // `code` or `code:detail` (see the server's `refuse`).
      const [head = '', detail] = message.split(':');
      const known = ErrorCodeSchema.safeParse(head);
      if (known.success) {
        throw new JoinError(known.data, detail && ROOM_ID.test(detail) ? detail : undefined);
      }
      // The library's own words for a room that is not there, or has no place.
      if (/not found|invalid room/i.test(message)) throw new JoinError('notFound');
      if (/locked|full/i.test(message)) throw new JoinError('roomFull');
      throw new JoinError('unreachable');
    }
  }

  // ---- What the server says -----------------------------------------------------------

  private attach(room: Room): void {
    this.room = room;
    this.leaving = false;
    this.patch({ code: room.roomId });

    for (const type of Object.keys(ServerMessages) as ServerMessageType[]) {
      room.onMessage(type, (raw: unknown) => {
        if (this.room !== room) return;
        const parsed = ServerMessages[type].safeParse(raw);
        if (parsed.success) this.receive(type, parsed.data);
      });
    }

    room.onDrop(() => {
      if (this.room === room && !this.leaving) this.patch({ status: 'reconnecting' });
    });
    room.onReconnect(() => {
      if (this.room === room) this.patch({ status: this.statusOf(this.state.lobby) });
    });
    room.onLeave((code: number) => {
      if (this.room !== room) return;
      this.room = null;
      if (this.leaving || code === CONSENTED) {
        this.patch({ status: 'closed' });
        return;
      }
      void this.recover(room.roomId);
    });
  }

  private statusOf(lobby: ServerMessageOf<'lobby'> | null): ConnectionStatus {
    if (!lobby) return 'lobby';
    return lobby.phase;
  }

  private receive<K extends ServerMessageType>(type: K, message: ServerMessageOf<K>): void {
    switch (type) {
      case 'hello': {
        const hello = message as ServerMessageOf<'hello'>;
        this.patch({ you: hello.you, code: hello.code, mode: hello.mode });
        return;
      }
      case 'lobby': {
        const lobby = message as ServerMessageOf<'lobby'>;
        this.patch({
          lobby,
          lobbyAt: Date.now(),
          status: this.state.status === 'reconnecting' ? 'reconnecting' : lobby.phase,
        });
        return;
      }
      case 'view': {
        const view = message as ServerMessageOf<'view'>;
        this.patch({ view: view.view as unknown as PlayerView, seq: view.seq });
        return;
      }
      case 'chat': {
        const chat = message as ServerMessageOf<'chat'>;
        this.patch({ chat: [...this.state.chat, chat].slice(-MAX_CHAT) });
        return;
      }
      case 'emote': {
        const emote = message as ServerMessageOf<'emote'>;
        // The page's own clock, so that "a few seconds ago" does not depend on the server's.
        this.patch({
          emotes: [...this.state.emotes, { ...emote, at: Date.now() }].slice(-MAX_EMOTES),
        });
        return;
      }
      case 'error': {
        const error = message as ServerMessageOf<'error'>;
        this.flag(error.code, error.message);
        return;
      }
      case 'kicked': {
        this.patch({ kicked: true });
        return;
      }
      case 'settlement': {
        this.patch({ settlement: message as ServerMessageOf<'settlement'> });
        return;
      }
    }
  }

  /** The library gave up: join the room again by its code while the game is still on. */
  private async recover(code: string): Promise<void> {
    if (this.recovering) return;
    this.recovering = true;
    this.patch({ status: 'reconnecting' });
    const delays = this.options.rejoinDelaysMs ?? [1_000, 3_000, 6_000, 10_000];
    try {
      for (const wait of delays) {
        await new Promise((resolve) => setTimeout(resolve, wait));
        if (this.leaving) return;
        try {
          const room = await this.join((client, base) =>
            client.joinById(code, { ...base, mode: 'private' } satisfies JoinOptions),
          );
          this.attach(room);
          this.patch({ status: this.statusOf(this.state.lobby) });
          return;
        } catch (error) {
          // The room is gone, or the seat is no longer ours: no use going on.
          if (error instanceof JoinError && error.issue !== 'unreachable') {
            this.patch({ status: 'closed' });
            this.flag(error.issue);
            return;
          }
        }
      }
      this.patch({ status: 'closed' });
      this.flag('connectionLost');
    } finally {
      this.recovering = false;
    }
  }

  // ---- What the player does -----------------------------------------------------------

  private send<K extends ClientMessageType>(type: K, message: ClientMessageOf<K>): void {
    this.room?.send(type, message);
  }

  setReady(ready: boolean): void {
    this.send('ready', { ready });
  }

  patchConfig(config: Partial<LobbyConfig>): void {
    this.send('config', ClientMessages.config.parse(config));
  }

  start(): void {
    this.send('start', {});
  }

  kick(playerId: string): void {
    this.send('kick', { playerId });
  }

  /** In the role draft: takes a role (when it is this player's turn). */
  pickRole(role: Role): void {
    this.send('pick', { role });
  }

  say(text: string): void {
    this.send('chat', { text });
  }

  /** An emote to the table. */
  emote(id: ClientMessageOf<'emote'>['id']): void {
    this.send('emote', { id });
  }

  /** An action of the game; the server says (as an error) if the rules refuse it. */
  act(action: Action): void {
    this.send('intent', { action: action as ClientMessageOf<'intent'>['action'] });
  }

  /**
   * For tests: cuts the connection the way a network failure would (nobody says goodbye). With
   * `autoReconnect` off the library does not mend it, so that the second layer can be tried.
   */
  debugCut(autoReconnect = true): void {
    const room = this.room;
    if (!room) return;
    room.reconnection.enabled = autoReconnect;
    // The library waits five seconds of uptime before it mends a connection by itself.
    room.reconnection.minUptime = 0;
    const transport = room.connection.transport as unknown as {
      ws: { terminate?: () => void; close: () => void };
    };
    if (transport.ws.terminate) transport.ws.terminate();
    else transport.ws.close();
  }

  // ---- Getting out --------------------------------------------------------------------

  private async close(): Promise<void> {
    const room = this.room;
    this.leaving = true;
    this.room = null;
    if (room) {
      await Promise.race([
        room.leave(true).catch(() => undefined),
        new Promise((resolve) => setTimeout(resolve, 1_500)),
      ]);
    }
  }

  /** Leaves the room for good (a bot takes the seat). */
  async leave(): Promise<void> {
    await this.close();
    this.patch({ ...INITIAL });
  }

  dispose(): void {
    void this.leave();
    this.listeners.clear();
  }
}
